import * as THREE from 'three'

// Material for the winning line
const winLineMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,  // White
    transparent: true,
    opacity: 0.3,
    side: THREE.DoubleSide,  // Make it visible from both sides
    depthTest: true,  // Enable proper depth testing
    depthWrite: true  // Enable writing to the depth buffer
});

let winLine = null;  // Store reference to the winning line cylinder
import { XRButton } from 'three/addons/webxr/XRButton.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { FontLoader } from 'three/addons/loaders/FontLoader.js';
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';
import gsap from 'gsap';

// Configure GSAP for optimal performance with Three.js
gsap.ticker.lagSmoothing(0);
gsap.ticker.fps(60);

// Disable all console.log output
console.log = function(){};

// Store the font and current text mesh after loading to avoid reloading
let gameFont = null;
let currentText = null;

// Function to create and add 3D text
function create3DText(text, position = { x: 0, y: 0, z: 0 }) {
    function build(font) {
        // Cleanly remove & dispose the previous text
        if (currentText) {
            currentText.geometry?.dispose();
            // If material is an array, dispose each
            if (Array.isArray(currentText.material)) {
                currentText.material.forEach(m => m.dispose?.());
            } else {
                currentText.material?.dispose?.();
            }
            currentText.parent?.remove(currentText);
            currentText = null;
        }

        const textGeometry = new TextGeometry(text, {
            font,
            size: 0.05,
            height: 0,
            curveSegments: 12,
            bevelEnabled: false
        });

        // Center the text geometry
        textGeometry.computeBoundingBox();
        const w = textGeometry.boundingBox.max.x - textGeometry.boundingBox.min.x;

        const textMaterial = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            metalness: 0.3,
            roughness: 0.4,
            emissive: 0xffffff,
            emissiveIntensity: 0.5,
            depthTest: false,     // <- ensures it renders on top
            depthWrite: false     // <- avoids z-fighting artifacts
        });
        
        const mesh = new THREE.Mesh(textGeometry, textMaterial);
        mesh.name = 'gameText';
        
        // Position the text
        mesh.position.set(
            position.x - w / 2,
            position.y,
            position.z
        );

        // Add the text mesh
        baseComposition.add(mesh);
        currentText = mesh;
    }

    // If we already have the font loaded, use it directly
    if (gameFont) {
        build(gameFont);
    } else {
        // Load the font only once
        const fontLoader = new FontLoader();
        fontLoader.load('/static/fonts/helvetiker_regular.typeface.json', function(font) {
            gameFont = font; // Store the font for future use
            build(font);
        });
    }
}

let renderer;
let lPinchOn;
let rPinchOn;
let baseComposition;
let baseGrid;
let delta;
let activeSphere = null;

// Game state tracking
const GRID_SIZE = 3;
const MOVE_COOLDOWN = 800; // 800ms cooldown between moves
let lastMoveTime = 0;
let waitingForPinchRelease = false; // Track if we need to wait for pinch release
const gameState = Array(GRID_SIZE).fill().map(() => 
    Array(GRID_SIZE).fill().map(() => 
        Array(GRID_SIZE).fill(null)
    )
);

// Map to store sphere positions in the grid
const spherePositions = new Map(); // Map sphere UUID -> {x, y, z} grid coordinates
const usedSpheres = new Map(); // Map to store sphere UUID -> 'O' or 'X'

// Initialize sphere positions when loading the model
function initializeSpherePositions() {
    // Reset positions
    spherePositions.clear();
    
    // Create a map to track which positions we've found
    const foundPositions = new Set();
    
    // First pass: Map all dots we find
    baseGrid.traverse((child) => {
        if (child.isMesh && child.name && child.name.startsWith('Sphere_')) {
            const dotName = child.name.replace('Sphere_', 'Dot_');
            const coords = dotName.split('_')[1];
            if (coords && coords.length === 3) {
                const x = parseInt(coords[0]);
                const y = parseInt(coords[1]);
                const z = parseInt(coords[2]);
                
                if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
                    spherePositions.set(child.uuid, {x, y, z});
                    foundPositions.add(`${x},${y},${z}`);
                    console.log(`Found position ${x},${y},${z} from ${child.name}`);
                }
            }
        }
    });
    
    // Check which positions are missing
    console.log('\nGrid coverage analysis:');
    let missingPositions = [];
    for (let x = 0; x < GRID_SIZE; x++) {
        for (let y = 0; y < GRID_SIZE; y++) {
            for (let z = 0; z < GRID_SIZE; z++) {
                const key = `${x},${y},${z}`;
                if (!foundPositions.has(key)) {
                    missingPositions.push(key);
                    console.log(`Missing position: ${key}`);
                }
            }
        }
    }
    
    if (missingPositions.length > 0) {
        console.warn(`Warning: Missing ${missingPositions.length} grid positions:`, missingPositions);
    } else {
        console.log('All grid positions are covered!');
    }
    
    // Log found positions for verification
    console.log('\nFound positions:', Array.from(foundPositions).sort());
}

// Check for a win in all possible directions
function checkWin() {
    const directions = [
        // Horizontal lines (9)
        ...[0,1,2].flatMap(y => [0,1,2].map(z => ({dx:1, dy:0, dz:0, x:0, y, z}))),
        // Vertical lines (9)
        ...[0,1,2].flatMap(x => [0,1,2].map(z => ({dx:0, dy:1, dz:0, x, y:0, z}))),
        // Depth lines (9)
        ...[0,1,2].flatMap(x => [0,1,2].map(y => ({dx:0, dy:0, dz:1, x, y, z:0}))),
        // Diagonal in each face (6)
        ...[0,1,2].map(z => ({dx:1, dy:1, dz:0, x:0, y:0, z})),
        ...[0,1,2].map(z => ({dx:1, dy:-1, dz:0, x:0, y:2, z})),
        ...[0,1,2].map(y => ({dx:1, dy:0, dz:1, x:0, y, z:0})),
        ...[0,1,2].map(y => ({dx:1, dy:0, dz:-1, x:0, y, z:2})),
        ...[0,1,2].map(x => ({dx:0, dy:1, dz:1, x, y:0, z:0})),
        ...[0,1,2].map(x => ({dx:0, dy:1, dz:-1, x, y:0, z:2})),
        // Main diagonals through cube (4)
        [{dx:1, dy:1, dz:1, x:0, y:0, z:0}],
        [{dx:1, dy:1, dz:-1, x:0, y:0, z:2}],
        [{dx:1, dy:-1, dz:1, x:0, y:2, z:0}],
        [{dx:1, dy:-1, dz:-1, x:0, y:2, z:2}]
    ].flat();

    for (const dir of directions) {
        const line = [];
        let {x, y, z, dx, dy, dz} = dir;
        
        // Check each position in the line
        for (let i = 0; i < 3; i++) {
            if (x >= 0 && x < 3 && y >= 0 && y < 3 && z >= 0 && z < 3) {
                line.push(gameState[x][y][z]);
            }
            x += dx;
            y += dy;
            z += dz;
        }
        
        // Check if all positions are the same and not null
        if (line.length === 3 && line[0] !== null && line.every(val => val === line[0])) {
            console.log(`Winner: ${line[0]} in direction:`, dir);
            
            console.log('Creating winning line...');
            // Get the world positions of the first and last pieces in the winning line
            const startPiece = findSphereByGridPos(dir.x, dir.y, dir.z);
            const endPiece = findSphereByGridPos(
                dir.x + 2 * dir.dx,
                dir.y + 2 * dir.dy,
                dir.z + 2 * dir.dz
            );
            
            console.log('Start piece:', startPiece?.position);
            console.log('End piece:', endPiece?.position);

            if (startPiece && endPiece) {
                // Create the winning line between the pieces using the mesh objects
                createWinningLine(startPiece, endPiece);
            }

            return line[0];
        }
    }
    
    // Check for draw
    const isFull = gameState.every(plane => 
        plane.every(row => 
            row.every(cell => cell !== null)
        )
    );
    
    if (isFull) {
        console.log("Game is a draw!");
        return 'draw';
    }
    
    return null;
}

// Function to find a random free sphere
function getRandomFreeSphere() {
    const freeSpheres = [];
    baseGrid.traverse((child) => {
        if (child.isMesh && child.name && child.name.startsWith('Sphere_') && !usedSpheres.has(child.uuid) && child.visible) {
            freeSpheres.push(child);
        }
    });
    
    if (freeSpheres.length === 0) return null;
    return freeSpheres[Math.floor(Math.random() * freeSpheres.length)];
}

// Variables to track game state and animations
let isGameOver = false;
let isButtonAnimating = false;  // Track if button animation is in progress
let winner = null;  // Track who won the game ('O', 'X', or 'draw')

// Function to update game state
function updateGameState(sphereUUID, player) {
    if (isGameOver) return; // Don't update if game is over

    const pos = spherePositions.get(sphereUUID);
    if (pos) {
        gameState[pos.x][pos.y][pos.z] = player;
        console.log('Game state updated:', pos, player);
        console.log(gameState);
        
        // Check for win after each move
        const result = checkWin();
        if (result) {
            isGameOver = true; // Set game over flag
            winner = result; // Store who won
            if (result === 'draw') {
                console.log('Game ended in a draw!');
                // TODO: Handle draw state UI
            } else {
                console.log(`Player ${result} wins!`);
                
                // Show the appropriate win plane with GSAP animation
                if (result === 'O') {
                    gsap.to(endGameOPlane.scale, {
                        x: 1.2,
                        y: 1.2,
                        z: 1.2,
                        duration: 0.5,
                        ease: "power3.out"
                    });
                } else if (result === 'X') {
                    gsap.to(endGameXPlane.scale, {
                        x: 1.2,
                        y: 1.2,
                        z: 1.2,
                        duration: 0.5,
                        ease: "power3.out"
                    });
                }
                
                // Show the button plane with a delay
                gsap.to(endGameButtonPlane.scale, {
                    x: 1.2,
                    y: 1.2,
                    z: 1.2,
                    duration: 0.5,
                    delay: 0.2,
                    ease: "power3.out"
                });
            }
        }
    }
}

// Function to count pieces in a line
function countInLine(line, player) {
    const playerCount = line.filter(cell => cell === player).length;
    const emptyCount = line.filter(cell => cell === null).length;
    const opponentCount = line.filter(cell => cell !== null && cell !== player).length;
    return { playerCount, emptyCount, opponentCount };
}

// Function to get all possible lines through a position
function getLinesThrough(x, y, z) {
  const lines = [];

  // 13 unique directions in a 3x3x3 grid
  const dirs = [
    // axes
    {dx:1, dy:0, dz:0}, {dx:0, dy:1, dz:0}, {dx:0, dy:0, dz:1},
    // face diagonals
    {dx:1, dy:1, dz:0}, {dx:1, dy:-1, dz:0},
    {dx:1, dy:0, dz:1}, {dx:1, dy:0, dz:-1},
    {dx:0, dy:1, dz:1}, {dx:0, dy:1, dz:-1},
    // space diagonals
    {dx:1, dy:1, dz:1}, {dx:1, dy:1, dz:-1},
    {dx:1, dy:-1, dz:1}, {dx:1, dy:-1, dz:-1}
  ];

  // For each direction, slide a 3-cell window that MUST include (x,y,z)
  for (const {dx, dy, dz} of dirs) {
    // t is the index (−2, −1, 0) of the start of the 3-cell window relative to (x,y,z)
    for (let t = -2; t <= 0; t++) {
      const cells = [];
      let valid = true;

      for (let k = 0; k < 3; k++) {
        const px = x + (t + k) * dx;
        const py = y + (t + k) * dy;
        const pz = z + (t + k) * dz;

        if (px < 0 || px > 2 || py < 0 || py > 2 || pz < 0 || pz > 2) {
          valid = false;
          break;
        }
        cells.push({x: px, y: py, z: pz});
      }

      // Accept only if the middle of that 3-window actually includes (x,y,z)
      // i.e., 0 ∈ {t, t+1, t+2}
      if (valid && t <= 0 && t + 2 >= 0) {
        lines.push(cells.map(c => gameState[c.x][c.y][c.z]));
      }
    }
  }
  return lines;
}

// Debug function to visualize grid coordinates
function debugGridPosition(x, y, z, value) {
    console.log(`Grid Position [${x},${y},${z}]: ${value || 'empty'}`);
    // Debug visualization removed to avoid changing sphere colors during evaluation
}

// Function to evaluate a position's strategic value
function evaluatePosition(x, y, z, player) {
    debugGridPosition(x, y, z, player); // Debug visualization
    if (gameState[x][y][z] !== null) return -1; // Position already taken
    
    let score = 0;
    const opponent = player === 'X' ? 'O' : 'X';
    
    // Get all possible lines through this position
    const lines = getLinesThrough(x, y, z);
    
    for (const line of lines) {
        const counts = countInLine(line, player);
        
        // Winning move
        if (counts.playerCount === 2 && counts.emptyCount === 1) {
            return 1000;
        }
        
        // Blocking opponent's win
        const opponentCounts = countInLine(line, opponent);
        if (opponentCounts.playerCount === 2 && opponentCounts.emptyCount === 1) {
            return 900;
        }
        
        // Building potential wins
        if (counts.playerCount === 1 && counts.emptyCount === 2) {
            score += 50;
        }
        
        // Blocking opponent's potential wins
        if (opponentCounts.playerCount === 1 && opponentCounts.emptyCount === 2) {
            score += 40;
        }
    }
    
    // Strategic position scoring
    // Center is extremely valuable in 3D tic-tac-toe
    if (x === 1 && y === 1 && z === 1) score += 100;
    
    // Face centers are next most valuable
    else if ((x === 1 && y === 1) || (x === 1 && z === 1) || (y === 1 && z === 1)) score += 50;
    
    // Corners are also valuable
    else if ((x === 0 || x === 2) && (y === 0 || y === 2) && (z === 0 || z === 2)) score += 40;
    
    // Edges are least valuable but still important
    else score += 20;
    
    // Bonus for positions that could form multiple winning lines
    const possibleLines = getLinesThrough(x, y, z);
    const potentialWinningLines = possibleLines.filter(line => {
        const counts = countInLine(line, player);
        return counts.playerCount === 0 && counts.emptyCount === 3;
    }).length;
    
    score += potentialWinningLines * 30;
    
    return score;
}

// Function to evaluate a potential move
function evaluateMove(x, y, z, player) {
    // Temporarily make the move
    const originalValue = gameState[x][y][z];
    gameState[x][y][z] = player;
    
    // Check if this move would win
    const result = checkWin();
    
    // Undo the move
    gameState[x][y][z] = originalValue;
    
    return result === player;
}

// Function to find sphere by grid coordinates
// Function to create and animate the winning line
function createWinningLine(startPiece, endPiece) {
    // Get world positions of the pieces
    const startWorldPos = new THREE.Vector3();
    const endWorldPos = new THREE.Vector3();
    
    // Get the world position relative to baseGrid
    startPiece.getWorldPosition(startWorldPos);
    endPiece.getWorldPosition(endWorldPos);
    
    // Convert world positions to local positions relative to baseGrid
    const startLocalPos = baseGrid.worldToLocal(startWorldPos.clone());
    const endLocalPos = baseGrid.worldToLocal(endWorldPos.clone());
    
    // Calculate the midpoint in local space
    const midPoint = new THREE.Vector3().addVectors(startLocalPos, endLocalPos).multiplyScalar(0.5);
    
    // Calculate the distance in local space
    const distance = startLocalPos.distanceTo(endLocalPos);

    // Create cylinder geometry with proper scale for the grid
    const geometry = new THREE.CylinderGeometry(0.04, 0.04, distance, 16);

    // Create the cylinder mesh
    const cylinder = new THREE.Mesh(geometry, winLineMaterial);

    // Position the cylinder at the midpoint in local space
    cylinder.position.copy(midPoint);

    // Orient the cylinder to point from start to end in local space
    cylinder.lookAt(endLocalPos);
    cylinder.rotateX(Math.PI / 2);

    // Add to baseGrid to maintain proper scaling and transformations
    baseGrid.add(cylinder);
    console.log('Added winning line to baseGrid');

    // Store reference
    winLine = cylinder;

    // Animate the cylinder growing from center
    gsap.from(cylinder.scale, {
        y: 0,
        duration: 0.3,
        ease: "power2.out"
    });

    return cylinder;
}

function findSphereByGridPos(x, y, z) {
    let foundSphere = null;
    for (const [uuid, pos] of spherePositions.entries()) {
        if (pos.x === x && pos.y === y && pos.z === z) {  // Remove the usedSpheres check since we want to find placed pieces
            baseGrid.traverse((child) => {
                if (child.isMesh && child.uuid === uuid) {
                    foundSphere = child;
                    console.log('Found sphere at position:', x, y, z, 'UUID:', uuid);
                }
            });
            break;
        }
    }
    return foundSphere;
}

// Function to handle computer's turn
function computerTurn() {
    let bestScore = -1;
    let bestMove = null;
    
    // Evaluate all possible moves
    for (let x = 0; x < GRID_SIZE; x++) {
        for (let y = 0; y < GRID_SIZE; y++) {
            for (let z = 0; z < GRID_SIZE; z++) {
                const score = evaluatePosition(x, y, z, 'X');
                console.log(`Evaluating position ${x},${y},${z}: score ${score}`);
                
                if (score > bestScore) {
                    const sphere = findSphereByGridPos(x, y, z);
                    if (sphere) {
                        bestScore = score;
                        bestMove = sphere;
                    }
                }
            }
        }
    }
    
    // Make the best move found
    if (bestMove) {
        console.log(`Computer choosing move with score: ${bestScore}`);
        makeComputerMove(bestMove);
    } else {
        // Fallback to random move if something went wrong
        console.log('Falling back to random move');
        const freeSphere = getRandomFreeSphere();
        if (freeSphere) {
            makeComputerMove(freeSphere);
        }
    }
}

// Helper function to make computer's move
function makeComputerMove(sphere) {
    // Get the world position of the sphere
    const worldPos = new THREE.Vector3();
    sphere.getWorldPosition(worldPos);
    
    // Convert world position to local position relative to baseGrid
    const localPos = worldPos.clone();
    baseGrid.worldToLocal(localPos);
    
    // Spawn X model at the sphere's position
    spawnXModel(localPos);
    
    // Mark sphere as used
    usedSpheres.set(sphere.uuid, 'X');
    sphere.visible = false;
    
    // Update game state
    updateGameState(sphere.uuid, 'X');
}


// Chair rotation variables
let leftHandRotationActive = false;
let rightHandRotationActive = false;
let lastRightHandX = 0;
let lastLeftHandX = 0;
let lastRightHandY = 0;
let lastLeftHandY = 0;
let chairRotationSpeed = 4.0; // Maximum sensitivity - ultra fast and dramatic rotation!
let maxUpDownRotation = Math.PI / 3; // Limit up/down rotation to 60 degrees (π/3 radians) to prevent gimbal lock

// Helper function to clamp rotation values
function clampRotation(rotation, min, max) {
    return Math.max(min, Math.min(max, rotation));
}

// Scene
const scene = new THREE.Scene()
scene.background = new THREE.Color( 0x505050 );

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Camera
const camera = new THREE.PerspectiveCamera( 50, window.innerWidth / window.innerHeight, 0.1, 50 );
camera.position.set( 0, 1.6, 3 );
scene.add(camera)

// Renderer
renderer = new THREE.WebGLRenderer( { antialias: true } );
renderer.setPixelRatio( window.devicePixelRatio );
renderer.setSize( window.innerWidth, window.innerHeight );
renderer.xr.enabled = true;
document.body.appendChild( renderer.domElement );
// Create XR button but hide it (we'll use the whole page instead)
const xrButton = XRButton.createButton( renderer, { 'optionalFeatures': [ 'hand-tracking' ] } );
// Completely hide the button
xrButton.style.display = 'none';
xrButton.style.opacity = '0';
xrButton.style.visibility = 'hidden';
xrButton.style.pointerEvents = 'none';
xrButton.style.position = 'absolute';
xrButton.style.width = '0';
xrButton.style.height = '0';
xrButton.style.padding = '0';
xrButton.style.margin = '0';
xrButton.style.border = 'none';
xrButton.style.overflow = 'hidden';
document.body.appendChild( xrButton );

// Make the entire page clickable for entering WebXR
document.body.style.cursor = 'pointer';
document.body.addEventListener('click', () => {
    // Simply click the hidden XR button to trigger WebXR session
    xrButton.click();
});

// Add visual feedback for hover
document.body.addEventListener('mouseenter', () => {
    document.body.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
});
document.body.addEventListener('mouseleave', () => {
    document.body.style.backgroundColor = '';
});

// Base Composition (container for the chair)
const tempGeo = new THREE.BoxGeometry(0.11, 0.11, 0.11);
const tempMat = new THREE.MeshBasicMaterial({ 
    color: 0xffffff, 
    transparent: true,
    opacity: 0.0,
    depthTest: false,
    depthWrite: false
});
baseComposition = new THREE.Mesh(tempGeo, tempMat);
baseComposition.scale.set(1, 1, 1); // Scale the container cube to be thin
scene.add(baseComposition);

// Add the 3D text as a child of baseComposition
// Initial text is hidden - only shown for win/draw messages

// Load the texture for the end game plane
const textureLoader = new THREE.TextureLoader();
const winsTexture = textureLoader.load('/static/ui_wins.png');
const buttonTexture = textureLoader.load('/static/ui_button.png');
winsTexture.encoding = THREE.sRGBEncoding;
buttonTexture.encoding = THREE.sRGBEncoding;

// Create end game state planes
const endGameOPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(0.2592, 0.076464),  // Width and height with 576:170 proportion (90% of previous size)
    new THREE.MeshBasicMaterial({
        map: winsTexture,
        transparent: true,
        opacity: 1.0,
        side: THREE.DoubleSide
    })
);
endGameOPlane.name = 'endGameOPlane';
endGameOPlane.position.set(0, 0.12, 0);  // Slightly above the game board
endGameOPlane.scale.set(0, 0, 0);  // Start with zero scale
endGameOPlane.visible = true  // Keep it visible but scaled to 0
baseComposition.add(endGameOPlane);

// Create X win plane
const endGameXPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(0.2592, 0.076464),  // Same dimensions as O plane
    new THREE.MeshBasicMaterial({
        map: winsTexture,
        transparent: true,
        opacity: 1.0,
        side: THREE.DoubleSide
    })
);
endGameXPlane.name = 'endGameXPlane';
endGameXPlane.position.set(0, 0.12, 0);  // Same position as O plane
endGameXPlane.scale.set(0, 0, 0);  // Start with zero scale
endGameXPlane.visible = true  // Keep it visible but scaled to 0
baseComposition.add(endGameXPlane);

// Create second end game state plane
const endGameButtonPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(0.139968, 0.0401436),  // Width and height with 328:94 proportion (90% of previous size)
    new THREE.MeshBasicMaterial({
        map: buttonTexture,  // Button texture
        transparent: true,
        opacity: 1.0,
        side: THREE.DoubleSide
    })
);
endGameButtonPlane.name = 'endGameButtonPlane';
endGameButtonPlane.position.set(0, 0.04, 0);  // Slightly above center
endGameButtonPlane.scale.set(0, 0, 0);  // Start with zero scale
endGameButtonPlane.visible = true  // Keep it visible but scaled to 0
baseComposition.add(endGameButtonPlane);

// Auto-position composition at user's head height
let compositionPositioned = false;
let xrSessionStartTime = null;

function positionCompositionAtHeadHeight() {
    if (compositionPositioned || !xrSessionStartTime) return;
    
    // Wait 2 seconds after WebXR session starts
    const elapsedTime = Date.now() - xrSessionStartTime;
    if (elapsedTime < 1000) {
        return; // Wait for 1 seconds
    }
    
    // Get user's head position (camera position)
    const headHeight = camera.position.y;
    const headDistance = camera.position.z;
    
    console.log('After 2 seconds in WebXR - Camera position:', camera.position);
    console.log('Head height:', headHeight, 'Head distance:', headDistance);
    
    // Position composition at head height, slightly in front of user
    baseComposition.position.set(0, headHeight, headDistance - 1.0);
    
    compositionPositioned = true;
    console.log('Composition positioned at:', baseComposition.position);
    console.log('Final height:', headHeight - 0.2, 'Final distance:', headDistance - 1.0);
}

// Listen for WebXR session start
renderer.xr.addEventListener('sessionstart', () => {
    xrSessionStartTime = Date.now();
    compositionPositioned = false;
    // Add class to hide the instruction overlay
    document.body.classList.add('xr-presenting');
});

renderer.xr.addEventListener('sessionend', () => {
    xrSessionStartTime = null;
    compositionPositioned = false; // Reset for next session
    // Remove class to show the instruction overlay again
    document.body.classList.remove('xr-presenting');
});

// Load the tictactoe.glb model
const loader = new GLTFLoader();
loader.load(
    '/static/tictactoe.glb',
    function (gltf) {
        console.log('TicTacToe GLB model loaded successfully!');
        
        // Get the model from the loaded GLTF
        baseGrid = gltf.scene;
        
        // Keep original model scale
        baseGrid.scale.set(0.11, 0.11, 0.11);
        
        // Position the model relative to the base composition
        baseGrid.position.set(0, -0.21, 0);
        
        // Set initial rotation
        baseGrid.rotation.set(0, 0, 0);
        
        // Make the model invisible by traversing all its meshes except spheres
        baseGrid.traverse((child) => {
            if (child.isMesh && (!child.name || !child.name.startsWith('Sphere_'))) {
                child.visible = false;
            }
        });
        
        // Add the model as a child of the base composition
        baseComposition.add(baseGrid);
        
        // Initialize sphere positions for game state tracking
        initializeSpherePositions();

        // Debug: List all objects in the model
        console.log('All objects in the model:');
        baseGrid.traverse((child) => {
            if (child.isMesh) {
                console.log('Found mesh:', child.name);
            }
        });

        // After GLB is loaded, find all Dot_XXX parts and add spheres
        baseGrid.traverse((child) => {
            if (child.isMesh && child.name && child.name.startsWith('Dot_')) {
                console.log('Processing dot:', child.name, 'at position:', child.position.toArray());
                // Create sphere at the exact position of the Dot
                const sphereGeometry = new THREE.SphereGeometry(0.05, 16, 16);
                const sphereMaterial = new THREE.MeshBasicMaterial({ 
                    color: 0xffffff,
                    transparent: true,
                    opacity: 0.8 
                });
                const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
                
                // Keep the same coordinate system in the name
                sphere.name = child.name.replace('Dot_', 'Sphere_');
                
                // Copy the exact position of the Dot
                sphere.position.copy(child.position);
                
                // Add sphere as a child to the same parent as the Dot
                child.parent.add(sphere);
                
                // Extract coordinates and record sphere position
                const coords = child.name.split('_')[1];
                if (coords && coords.length === 3) {
                    const x = parseInt(coords[0], 10);
                    const y = parseInt(coords[1], 10);
                    const z = parseInt(coords[2], 10);
                    spherePositions.set(sphere.uuid, { x, y, z });
                    console.log(`Added sphere ${sphere.name} at position of ${child.name} (${x},${y},${z}):`, child.position.toArray());
                }
            }
        });

        console.log('TicTacToe model added to scene:', baseGrid);
        console.log('Model rotation Y:', baseGrid.rotation.y);
    },
    function (progress) {
        console.log('Loading progress:', (progress.loaded / progress.total * 100) + '%');
    },
    function (error) {
        console.error('Error loading tictactoe.glb:', error);
        
        // Fallback to a simple cube if GLB loading fails
        console.log('Creating fallback cube...');
        const cubeGeometry = new THREE.BoxGeometry(0.3, 0.3, 0.3);
        const cubeMaterial = new THREE.MeshLambertMaterial({ 
            color: 0x808080,
            transparent: false
        });
        baseGrid = new THREE.Mesh(cubeGeometry, cubeMaterial);
        baseGrid.scale.set(0.15, 0.15, 0.15);
        baseGrid.position.set(0, -0.22, 0);
        baseGrid.rotation.set(0, 0, 0);
        baseComposition.add(baseGrid);
        console.log('Fallback cube created successfully!');
    }
);

// Define materials first
const orangeMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xFF9737,   // Lighter warm orange
    metalness: 0.25,   // Slightly increased metallic for more reflections
    roughness: 0.4,    // Decreased roughness for more shine
    sheen: 0.6,        // Velvety falloff
    sheenRoughness: 0.6,
    clearcoat: 0.45,   // Increased clearcoat for more reflection
    clearcoatRoughness: 0.45, // Smoother clearcoat for more shine
    emissive: 0xFF9737,
    emissiveIntensity: 0.25,  // Maintained soft glow
    envMapIntensity: 1.3     // Increased environment reflection
});

const lightBlueMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x60AEFF,   // Lighter bright blue (#60AEFF)
    metalness: 0.25,   // Slightly increased metallic for more reflections
    roughness: 0.4,    // Decreased roughness for more shine
    emissive: 0x60AEFF,
    emissiveIntensity: 0.25,  // Maintained soft glow
    clearcoat: 0.45,   // Increased clearcoat for more reflection
    clearcoatRoughness: 0.45, // Smoother clearcoat for more shine
    envMapIntensity: 1.3     // Increased environment reflection
});

// Add orange sphere to endGameOPlane
const uiSphereGeometry = new THREE.SphereGeometry(0.025, 32, 32);  // Small sphere, high detail
const uiOSphere = new THREE.Mesh(uiSphereGeometry, orangeMaterial);
uiOSphere.position.set(-0.085, 0, 0.0);  // Position it to the right side of the plane, slightly in front
uiOSphere.name = 'uiOrangeSphere';
endGameOPlane.add(uiOSphere);

// Add X model to endGameXPlane (will be added once model is loaded)
function addXToEndGamePlane() {
    if (!xModelTemplate) {
        console.warn('X model template not loaded yet');
        return;
    }
    
    // Clone the X model template
    const uiX = xModelTemplate.clone();
    uiX.name = 'uiXModel';
    
    // Position it like the sphere was
    uiX.position.set(-0.085, 0, 0.0);
    
    // Scale it down significantly since it's for UI
    uiX.scale.set(0.08, 0.08, 0.08);
    
    // Rotate 45 degrees around Y axis
    uiX.rotation.y = Math.PI / 4;
    
    // Add it to the plane
    endGameXPlane.add(uiX);
}

// Load and store the O model template
let oModelTemplate = null;

// Load and store the X model template
let xModelTemplate = null;

const xLoader = new GLTFLoader();
xLoader.load(
    '/static/x.glb',
    function (gltf) {
        console.log('X model loaded successfully!');
        xModelTemplate = gltf.scene;
        
        // Apply the material to all meshes in the model
        xModelTemplate.traverse((child) => {
            if (child.isMesh) {
                child.material = lightBlueMaterial;
            }
        });
        
        // Set initial scale to 1.0
        xModelTemplate.scale.set(1.0, 1.0, 1.0);
        
        // Add X to the end game plane now that the model is loaded
        addXToEndGamePlane();
        
        console.log('X model template ready');
    },
    function (progress) {
        console.log('Loading X model progress:', (progress.loaded / progress.total * 100) + '%');
    },
    function (error) {
        console.error('Error loading X model:', error);
    }
);

const oLoader = new GLTFLoader();
oLoader.load(
    '/static/o.glb',
    function (gltf) {
        console.log('O model loaded successfully!');
        oModelTemplate = gltf.scene;
        
        // Apply the material to all meshes in the model
        oModelTemplate.traverse((child) => {
            if (child.isMesh) {
                child.material = orangeMaterial;
            }
        });
        
        // Set initial scale to 0.9
        oModelTemplate.scale.set(0.9, 0.9, 0.9);
        
        console.log('O model template ready');
    },
    function (progress) {
        console.log('Loading O model progress:', (progress.loaded / progress.total * 100) + '%');
    },
    function (error) {
        console.error('Error loading O model:', error);
    }
);

// Function to spawn O model at a given position
function spawnOModel(position) {
    if (!oModelTemplate) {
        console.warn('O model template not loaded yet');
        return null;
    }
    
    // Clone the template
    const oModel = oModelTemplate.clone();
    
    // Set the position
    oModel.position.copy(position);
    
    // Add as child to baseGrid so it rotates with it
    baseGrid.add(oModel);
    
    // Add a nice spawn animation
    oModel.scale.set(0.01, 0.01, 0.01); // Start tiny
    gsap.to(oModel.scale, {
        x: 0.9,
        y: 0.9,
        z: 0.9,
        duration: 0.3,
        ease: "back.out"
    });
    
    return oModel;
}

// Function to spawn X model at a given position
function spawnXModel(position) {
    if (!xModelTemplate) {
        console.warn('X model template not loaded yet');
        return null;
    }
    
    // Clone the template
    const xModel = xModelTemplate.clone();
    
    // Set the position
    xModel.position.copy(position);
    
    // Add as child to baseGrid so it rotates with it
    baseGrid.add(xModel);
    
    // Add a nice spawn animation
    xModel.scale.set(0.01, 0.01, 0.01); // Start tiny
    gsap.to(xModel.scale, {
        x: 1.0,
        y: 1.0,
        z: 1.0,
        duration: 0.3,
        ease: "back.out"
    });
    
    return xModel;
}

/**
 * Lights
 */
// Cool ambient light for base illumination
const ambientLight = new THREE.AmbientLight(0xb4c8ff, 1.4);  // Increased soft blue tint
scene.add(ambientLight);

// Main key light from above-right (warm)
const keyLight = new THREE.DirectionalLight(0xfff0dd, 2.8);  // Increased warmth and intensity
keyLight.position.set(2, 4, 2);
scene.add(keyLight);

// Fill light from left (cool)
const fillLight = new THREE.DirectionalLight(0xb4e5ff, 1.8);  // Increased cool blue
fillLight.position.set(-3, 2, 1);
scene.add(fillLight);

// Rim light from behind (very cool)
const rimLight = new THREE.DirectionalLight(0x8eb8ff, 1.2);  // Increased rim light
rimLight.position.set(-1, 3, -2);
scene.add(rimLight);

// Soft ground bounce light
const groundLight = new THREE.DirectionalLight(0xffffff, 0.4);  // Increased bounce
groundLight.position.set(0, -3, 0);
scene.add(groundLight);

/**
 * Joint References
 */
const sphere = new THREE.SphereGeometry(0.002, 32, 32)
const defaultMat = new THREE.MeshBasicMaterial({ 
    color: 0xff0000, 
    opacity: 0,
    transparent: true 
})

const lThumbObj = new THREE.Mesh(sphere, defaultMat)
const lIndexObj = new THREE.Mesh(sphere, defaultMat)
const rThumbObj = new THREE.Mesh(sphere, defaultMat)
const rIndexObj = new THREE.Mesh(sphere, defaultMat)

scene.add(lThumbObj)
scene.add(lIndexObj)
scene.add(rThumbObj)
scene.add(rIndexObj)

/**
 * Pinch References
 */
const sphere2 = new THREE.SphereGeometry(0.006, 32, 32)
const defaultMat2 = new THREE.MeshLambertMaterial({ 
    color: 0xffffff,
})
const greenMat2 = new THREE.MeshLambertMaterial({ 
    color: 0x00ff00,
})

const lPinchSphere = new THREE.Mesh(sphere2, defaultMat2)
const rPinchSphere = new THREE.Mesh(sphere2, greenMat2)
scene.add(lPinchSphere)
scene.add(rPinchSphere)
lPinchSphere.visible = false;
rPinchSphere.visible = false; // Hidden - no longer needed for debug

/**
 * Hands
 */
var hand1, hand2;


hand1 = renderer.xr.getHand( 0 );
scene.add( hand1 );

hand2 = renderer.xr.getHand( 1 );
scene.add( hand2 );

const handsGroup = new THREE.Group();
handsGroup.add(hand1, hand2);
scene.add(handsGroup);
handsGroup.visible = false;

const clock = new THREE.Clock();

renderer.setAnimationLoop( function () {
    delta = clock.getDelta();
    gsap.ticker.tick(delta);

    renderer.render( scene, camera );
	
    
    // Continuous rotation of the tictactoe model
    // if (baseGrid) {
    //     baseGrid.rotation.y += 0.01; // Adjust speed by changing this value
    // }
    
    
    handTracking();
    positionCompositionAtHeadHeight(); // Call this function in the render loop
} );

function findNearestSphere(position) {
    let nearestSphere = null;
    let minDistance = Infinity;
    
    baseGrid.traverse((child) => {
        if (child.isMesh && child.name && child.name.startsWith('Sphere_')) {
            const distance = position.distanceTo(child.getWorldPosition(new THREE.Vector3()));
            if (distance < minDistance) {
                minDistance = distance;
                nearestSphere = child;
            }
        }
    });
    
    // Only return the sphere if it's within a reasonable distance (0.1 units)
    return minDistance < 0.05 ? nearestSphere : null;
}

// Function to check if a point intersects with a plane
function checkPlaneIntersection(point, plane) {
    if (!plane.visible || plane.scale.x === 0) return false;
    
    // Get plane's world position and dimensions
    const worldPos = new THREE.Vector3();
    plane.getWorldPosition(worldPos);
    
    // Get plane's dimensions (accounting for scale)
    const width = plane.geometry.parameters.width * plane.scale.x;
    const height = plane.geometry.parameters.height * plane.scale.y;
    
    // Calculate bounds
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    
    // Convert point to plane's local space
    const localPoint = point.clone();
    plane.worldToLocal(localPoint);
    
    // Check if point is within bounds
    return (
        localPoint.x >= -halfWidth && localPoint.x <= halfWidth &&
        localPoint.y >= -halfHeight && localPoint.y <= halfHeight &&
        Math.abs(localPoint.z) < 0.02  // Small threshold for depth
    );
}

function handTracking() {
    // Force hand1 to always be left hand tracking, hand2 to be right
    // This gives you consistent control regardless of WebXR's internal assignment
    const leftHand = hand1;
    const rightHand = hand2;
    
    // Left Hand Joint References (using hand1 consistently)
    if (leftHand && leftHand.joints && leftHand.joints['thumb-tip']) {
        lThumbObj.position.x = leftHand.joints['thumb-tip'].position.x;
        lThumbObj.position.y = leftHand.joints['thumb-tip'].position.y;
        lThumbObj.position.z = leftHand.joints['thumb-tip'].position.z;
        lThumbObj.rotation.x = leftHand.joints['thumb-tip'].rotation.x;
        lThumbObj.rotation.y = leftHand.joints['thumb-tip'].rotation.y;
        lThumbObj.rotation.z = leftHand.joints['thumb-tip'].rotation.z;
        
        // Check for button interaction with left index finger
        if (leftHand.joints['index-finger-tip']) {
            const indexTip = new THREE.Vector3(
                leftHand.joints['index-finger-tip'].position.x,
                leftHand.joints['index-finger-tip'].position.y,
                leftHand.joints['index-finger-tip'].position.z
            );
            
            if (checkPlaneIntersection(indexTip, endGameButtonPlane)) {
                animateButtonPress();
            }
        }
    }

    if (leftHand && leftHand.joints && leftHand.joints['index-finger-tip']) {
        lIndexObj.position.x = leftHand.joints['index-finger-tip'].position.x;
        lIndexObj.position.y = leftHand.joints['index-finger-tip'].position.y;
        lIndexObj.position.z = leftHand.joints['index-finger-tip'].position.z;
        lIndexObj.rotation.x = leftHand.joints['index-finger-tip'].rotation.x;
        lIndexObj.rotation.y = leftHand.joints['index-finger-tip'].rotation.y;
        lIndexObj.rotation.z = leftHand.joints['index-finger-tip'].rotation.z;
    }

    // Right Hand Joint References (using hand2 consistently)
    if (rightHand && rightHand.joints && rightHand.joints['thumb-tip']) {
        rThumbObj.position.x = rightHand.joints['thumb-tip'].position.x;
        rThumbObj.position.y = rightHand.joints['thumb-tip'].position.y;
        rThumbObj.position.z = rightHand.joints['thumb-tip'].position.z;
        rThumbObj.rotation.x = rightHand.joints['thumb-tip'].rotation.x;
        rThumbObj.rotation.y = rightHand.joints['thumb-tip'].rotation.y;
        rThumbObj.rotation.z = rightHand.joints['thumb-tip'].rotation.z;
        
        // Check for button interaction with right index finger
        if (rightHand.joints['index-finger-tip']) {
            const indexTip = new THREE.Vector3(
                rightHand.joints['index-finger-tip'].position.x,
                rightHand.joints['index-finger-tip'].position.y,
                rightHand.joints['index-finger-tip'].position.z
            );
            
            if (checkPlaneIntersection(indexTip, endGameButtonPlane)) {
                animateButtonPress();
            }
        }
    }

    if (rightHand && rightHand.joints && rightHand.joints['index-finger-tip']) {
        rIndexObj.position.x = rightHand.joints['index-finger-tip'].position.x;
        rIndexObj.position.y = rightHand.joints['index-finger-tip'].position.y;
        rIndexObj.position.z = rightHand.joints['index-finger-tip'].position.z;
        rIndexObj.rotation.x = rightHand.joints['index-finger-tip'].rotation.x;
        rIndexObj.rotation.y = rightHand.joints['index-finger-tip'].rotation.y;
        rIndexObj.rotation.z = rightHand.joints['index-finger-tip'].rotation.z;
    }

    // Left Hand Pinch Detection
    const lIndexThumbDist = lIndexObj.position.distanceTo(lThumbObj.position);

    if (lIndexThumbDist < 0.02) {
        lPinchSphere.position.x = lThumbObj.position.x;
        lPinchSphere.position.y = lThumbObj.position.y;
        lPinchSphere.position.z = lThumbObj.position.z;
        
        // Left hand pinch detected
        lPinchOn = true;
        
// Check for nearby sphere to spawn O model
            const currentTime = Date.now();
            if (!activeSphere && !isGameOver && !waitingForPinchRelease && (currentTime - lastMoveTime) >= MOVE_COOLDOWN) {
                const nearestSphere = findNearestSphere(lThumbObj.position);
                if (nearestSphere && !usedSpheres.has(nearestSphere.uuid)) {
                    // Mark this position as used
                    usedSpheres.set(nearestSphere.uuid, 'O');
                    activeSphere = nearestSphere;
                    
                    // Get the world position of the sphere
                    const worldPos = new THREE.Vector3();
                    nearestSphere.getWorldPosition(worldPos);
                    
                    // Convert world position to local position relative to baseGrid
                    const localPos = worldPos.clone();
                    baseGrid.worldToLocal(localPos);
                    
                    // Spawn O model at the sphere's position
                    spawnOModel(localPos);
                    
                    // Hide the sphere
                    nearestSphere.visible = false;
                    activeSphere = null;
                    
                    // Update game state for player's move
                    updateGameState(nearestSphere.uuid, 'O');
                    
                    // Update last move time and set waiting for release
                    lastMoveTime = currentTime;
                    waitingForPinchRelease = true;
                    
                    // Add a small delay before computer's turn
                    if (!isGameOver) {
                        setTimeout(computerTurn, 500); // 500ms delay
                    }
                }
            }
        
        // TicTacToe model rotation interaction - start rotation when left hand pinches
        if (!leftHandRotationActive) {
            leftHandRotationActive = true;
            lastLeftHandX = lThumbObj.position.x;
            lastLeftHandY = lThumbObj.position.y;
        }
        
        // Calculate horizontal and vertical movement for tictactoe model rotation
        if (leftHandRotationActive) {
            const currentLeftHandX = lThumbObj.position.x;
            const currentLeftHandY = lThumbObj.position.y;
            const deltaX = currentLeftHandX - lastLeftHandX;
            const deltaY = currentLeftHandY - lastLeftHandY;
            
            
            // Rotate tictactoe model based on horizontal movement (Y-axis rotation)
            if (baseGrid && Math.abs(deltaX) > 0.001) {
                const oldRotationY = baseGrid.rotation.y;
                baseGrid.rotation.y += deltaX * chairRotationSpeed;
            }
            
            // Rotate tictactoe model based on vertical movement (X-axis rotation) - reversed direction
            if (baseGrid && Math.abs(deltaY) > 0.001) {
                const oldRotationX = baseGrid.rotation.x;
                const newRotationX = baseGrid.rotation.x - deltaY * chairRotationSpeed;
                baseGrid.rotation.x = clampRotation(newRotationX, -maxUpDownRotation, maxUpDownRotation);
            }
            
            lastLeftHandX = currentLeftHandX;
            lastLeftHandY = currentLeftHandY;
        }
    } else {
        // Reset pinch state when released
        if (lPinchOn) {
            lPinchOn = false;
            waitingForPinchRelease = false;
        }
        
        // Stop tictactoe model rotation when left hand stops pinching
        if (leftHandRotationActive) {
            leftHandRotationActive = false;
        }

        // Just clear active sphere reference
        if (activeSphere && !rPinchOn) {
            activeSphere = null;
        }
    }

    // Right Hand Pinch Detection
    const rIndexThumbDist = rIndexObj.position.distanceTo(rThumbObj.position);
    
    
    // Always update the pinch reference sphere position when pinching
    if (rIndexThumbDist < 0.02) {
        rPinchSphere.position.x = rThumbObj.position.x;
        rPinchSphere.position.y = rThumbObj.position.y;
        rPinchSphere.position.z = rThumbObj.position.z;
        rPinchOn = true;
        
        // Check for nearby sphere to spawn O model
        const currentTime = Date.now();
        if (!activeSphere && !isGameOver && !waitingForPinchRelease && (currentTime - lastMoveTime) >= MOVE_COOLDOWN) {
            const nearestSphere = findNearestSphere(rThumbObj.position);
            if (nearestSphere && !usedSpheres.has(nearestSphere.uuid)) {
                // Mark this position as used
                usedSpheres.set(nearestSphere.uuid, 'O');
                activeSphere = nearestSphere;
                
                // Get the world position of the sphere
                const worldPos = new THREE.Vector3();
                nearestSphere.getWorldPosition(worldPos);
                
                // Convert world position to local position relative to baseGrid
                const localPos = worldPos.clone();
                baseGrid.worldToLocal(localPos);
                
                // Spawn O model at the sphere's position
                spawnOModel(localPos);
                
                // Hide the sphere
                nearestSphere.visible = false;
                activeSphere = null;
                
                // Update game state for player's move (right hand)
                updateGameState(nearestSphere.uuid, 'O');
                
                // Update last move time and set waiting for release
                lastMoveTime = currentTime;
                waitingForPinchRelease = true;
                
                // Add a small delay before computer's turn
                if (!isGameOver) {
                    setTimeout(computerTurn, 500); // 500ms delay
                }
            }
        }
        
        // TicTacToe model rotation interaction - start rotation when right hand pinches
        if (!rightHandRotationActive) {
            rightHandRotationActive = true;
            lastRightHandX = rThumbObj.position.x;
            lastRightHandY = rThumbObj.position.y;
        }
        
        // Calculate horizontal and vertical movement for tictactoe model rotation
        if (rightHandRotationActive) {
            const currentRightHandX = rThumbObj.position.x;
            const currentRightHandY = rThumbObj.position.y;
            const deltaX = currentRightHandX - lastRightHandX;
            const deltaY = currentRightHandY - lastRightHandY;
            
            
            // Rotate tictactoe model based on horizontal movement (Y-axis rotation)
            if (baseGrid && Math.abs(deltaX) > 0.001) {
                const oldRotationY = baseGrid.rotation.y;
                baseGrid.rotation.y += deltaX * chairRotationSpeed;
            }
            
            // Rotate tictactoe model based on vertical movement (X-axis rotation) - reversed direction
            if (baseGrid && Math.abs(deltaY) > 0.001) {
                const oldRotationX = baseGrid.rotation.x;
                const newRotationX = baseGrid.rotation.x - deltaY * chairRotationSpeed;
                baseGrid.rotation.x = clampRotation(newRotationX, -maxUpDownRotation, maxUpDownRotation);
            }
            
            lastRightHandX = currentRightHandX;
            lastRightHandY = currentRightHandY;
        }
    } else {
        // Reset pinch state when released
        if (rPinchOn) {
            rPinchOn = false;
            waitingForPinchRelease = false;
        }
        
        // Stop tictactoe model rotation when right hand stops pinching
        if (rightHandRotationActive) {
            rightHandRotationActive = false;
        }

        // Just clear active sphere reference if this hand was controlling it
        if (activeSphere && !lPinchOn) {
            activeSphere = null;
        }
    }
    

}

// Function to animate button press
function animateButtonPress() {
    if (isButtonAnimating) return;
    isButtonAnimating = true;

    // Scale down with ease-out (fast start, smooth end)
    gsap.to(endGameButtonPlane.scale, {
        x: 0.7,
        y: 0.7,
        z: 0.7,
        duration: 0.2,
        ease: "power2.out",
        onComplete: () => {
            // Scale back up with ease-in (smooth start, fast end)
            gsap.to(endGameButtonPlane.scale, {
                x: 1.0,
                y: 1.0,
                z: 1.0,
                duration: 0.2,
                ease: "power2.in",
                onComplete: () => {
                    isButtonAnimating = false;
                    // Start game reset after button animation
                    console.log('Button animation complete, starting reset...');
                    resetGame();
                }
            });
        }
    });
}

// Function to reset the game state and UI
function resetGame() {
    console.log('Starting game reset sequence...');
    
    // Remove winning line if it exists
    if (winLine) {
        baseGrid.remove(winLine);
        winLine.geometry.dispose();
        winLine.material.dispose();
        winLine = null;
        console.log('Removed winning line');
    }
    
    // Wait for 2 seconds before starting reset animations
    setTimeout(() => {
        console.log('Beginning reset animations...');
        // 1. Scale down the win message plane
        if (winner === 'O') {
            gsap.to(endGameOPlane.scale, {
                x: 0,
                y: 0,
                z: 0,
                duration: 0.2,
                ease: "power3.in"
            });
        } else if (winner === 'X') {
            gsap.to(endGameXPlane.scale, {
                x: 0,
                y: 0,
                z: 0,
                duration: 0.2,
                ease: "power3.in"
            });
        }

        // 2. Scale down the button plane
        gsap.to(endGameButtonPlane.scale, {
            x: 0,
            y: 0,
            z: 0,
            duration: 0.2,
            ease: "power3.in"
        });

        // 3. Scale down and remove all X and O pieces
        console.log('Cleaning up game pieces...');
        let piecesFound = 0;
        baseGrid.traverse((child) => {
            if (child.isMesh) {
                // Check if this is an X or O piece (they are children of baseGrid and use our materials)
                if (child.material === lightBlueMaterial || child.material === orangeMaterial) {
                    piecesFound++;
                    console.log(`Found game piece to remove: ${child.material === lightBlueMaterial ? 'X' : 'O'}`);
                    const obj = child;
                    gsap.to(obj.scale, {
                        x: 0,
                        y: 0,
                        z: 0,
                        duration: 0.2,
                        ease: "power3.in",
                        onComplete: () => {
                            baseGrid.remove(obj);
                            obj.traverse?.((n) => {
                                if (n.isMesh) {
                                    n.geometry?.dispose?.();
                                    if (
                                        n.material &&
                                        n.material !== lightBlueMaterial &&
                                        n.material !== orangeMaterial
                                    ) {
                                        n.material.dispose?.();
                                    }
                                }
                            });
                        }
                    });
                }
                // Make all spheres visible again
                if (child.name && child.name.startsWith('Sphere_')) {
                    child.visible = true;
                }
            }
        });

        // Log final piece count
        console.log(`Total pieces found to remove: ${piecesFound}`);

        // 4. Reset game state variables
        gameState.forEach((plane, x) => {
            plane.forEach((row, y) => {
                row.forEach((_, z) => {
                    gameState[x][y][z] = null;
                });
            });
        });
        
        // Clear tracking maps
        usedSpheres.clear();
        
        // Reset game state flags
        isGameOver = false;
        lastMoveTime = 0;
        waitingForPinchRelease = false;
        activeSphere = null;
        winner = null;

        console.log('Game reset complete - ready for new game');
    }, 300); // 0.3 second delay
}

function rotateToCamera() {
    // Make the base composition face the camera along one horizontal axis (y-axis)
    let targetPosition = camera.position.clone();
    targetPosition.y = baseComposition.position.y; // Keep the y position same to rotate around y-axis only

    let targetQuaternion = new THREE.Quaternion();
    let currentQuaternion = baseComposition.quaternion.clone();

    // Calculate target quaternion for the base composition to look at the target position
    baseComposition.lookAt(targetPosition);
    targetQuaternion.copy(baseComposition.quaternion);
    baseComposition.quaternion.copy(currentQuaternion); // Revert to the original quaternion

    // Smoothly interpolate the rotation
    baseComposition.quaternion.slerp(targetQuaternion, 0.1);
}



